import React, { useState, useEffect } from 'react';
import { X, Ban, Plus, Trash2, Loader2 } from 'lucide-react';
import request from '../../api/request';

interface ExcludedOrdersModalProps {
    onClose: () => void;
}

const ExcludedOrdersModal: React.FC<ExcludedOrdersModalProps> = ({ onClose }) => {
    const [excludedNos, setExcludedNos] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // 删除订单分析记录相关状态
    const [trackingNosInput, setTrackingNosInput] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        loadExcludedNos();
    }, []);

    const loadExcludedNos = async () => {
        setIsLoading(true);
        try {
            const response = await request.get('/api/settings');
            const raw = response.data.orderPullExcludeNos || '[]';
            try {
                setExcludedNos(JSON.parse(raw));
            } catch {
                setExcludedNos([]);
            }
        } catch (e) {
            console.error('加载排除列表失败:', e);
        } finally {
            setIsLoading(false);
        }
    };

    const saveExcludedNos = async (list: string[]) => {
        setIsSaving(true);
        try {
            await request.put('/api/settings', {
                orderPullExcludeNos: JSON.stringify(list),
            });
            setExcludedNos(list);
        } catch (e: any) {
            alert('❌ 保存失败: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setIsSaving(false);
        }
    };

    const addExcludeNo = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        if (excludedNos.includes(trimmed)) {
            alert('该编号已在列表中');
            return;
        }
        const list = [...excludedNos, trimmed];
        saveExcludedNos(list);
        setInputValue('');
    };

    const removeExcludeNo = (refNo: string) => {
        const list = excludedNos.filter(n => n !== refNo);
        saveExcludedNos(list);
    };

    const handleDeleteTrackings = async () => {
        const trimmed = trackingNosInput.trim();
        if (!trimmed) {
            alert('请输入要删除的订单编号');
            return;
        }
        // 支持换行和逗号分隔
        const orderNos = trimmed
            .split(/[\n,]+/)
            .map(s => s.trim())
            .filter(s => s.length > 0);

        if (orderNos.length === 0) {
            alert('未识别到有效的订单编号');
            return;
        }

        if (!window.confirm(`确定要删除以下 ${orderNos.length} 个订单的分析记录吗？\n\n${orderNos.slice(0, 5).join('\n')}${orderNos.length > 5 ? `\n...等 ${orderNos.length - 5} 个` : ''}`)) {
            return;
        }

        setIsDeleting(true);
        try {
            const response = await request.delete('/api/settings/order-analysis/trackings/batch', {
                data: { order_nos: orderNos },
            });
            const data = response.data;
            let msg = data.message || `删除完成`;
            if (data.not_found_nos && data.not_found_nos.length > 0) {
                msg += `\n\n未找到的编号:\n${data.not_found_nos.slice(0, 10).join('\n')}`;
                if (data.not_found_nos.length > 10) {
                    msg += `\n...等 ${data.not_found_nos.length - 10} 个`;
                }
            }
            alert(msg);
            if (data.deleted_count > 0) {
                setTrackingNosInput('');
            }
        } catch (e: any) {
            alert('❌ 删除失败: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Ban className="w-5 h-5 text-destructive" />
                        排除订单管理
                    </h2>
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 flex-1 overflow-y-auto">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <>
                            {/* Add input */}
                            <div className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && addExcludeNo()}
                                    placeholder="输入 reference_no (如 E1866...)"
                                    className="flex-1 bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none"
                                />
                                <button
                                    onClick={addExcludeNo}
                                    disabled={isSaving}
                                    className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    添加
                                </button>
                            </div>

                            {/* List */}
                            {excludedNos.length === 0 ? (
                                <div className="text-center text-muted-foreground py-8 text-sm">
                                    暂无排除订单，添加后这些订单不会被拉取
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    <div className="text-xs text-muted-foreground mb-2">
                                        共 {excludedNos.length} 个排除订单
                                    </div>
                                    {excludedNos.map((refNo) => (
                                        <div
                                            key={refNo}
                                            className="flex items-center justify-between bg-muted rounded px-3 py-2 text-sm"
                                        >
                                            <span className="font-mono text-xs">{refNo}</span>
                                            <button
                                                onClick={() => removeExcludeNo(refNo)}
                                                disabled={isSaving}
                                                className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* 删除订单分析记录 */}
                <div className="border-t border-border px-4 pt-4">
                    <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
                        <Trash2 className="w-4 h-4 text-red-500" />
                        删除订单分析记录
                    </h3>
                    <p className="text-xs text-muted-foreground mb-2">
                        输入订单编号（每行一个或用逗号分隔），从订单分析表中删除对应记录
                    </p>
                    <textarea
                        value={trackingNosInput}
                        onChange={(e) => setTrackingNosInput(e.target.value)}
                        placeholder={"输入订单编号\n例如:\nE1866001\nE1866002\n或: E1866001,E1866002"}
                        rows={4}
                        className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-ring focus:outline-none resize-vertical font-mono"
                    />
                    <button
                        onClick={handleDeleteTrackings}
                        disabled={isDeleting || !trackingNosInput.trim()}
                        className="mt-2 flex items-center gap-1 px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                        {isDeleting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Trash2 className="w-4 h-4" />
                        )}
                        {isDeleting ? '删除中...' : '删除分析记录'}
                    </button>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                    <span>这些订单编号在拉取时会被跳过</span>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm hover:bg-muted rounded-md transition-colors"
                    >
                        关闭
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExcludedOrdersModal;
